import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { hasSelectedIntervention } from 'components/Assessments/utils/interventions'
import { AdapterOptions, FlatAnswerMutation } from '../../../adapters/types'
import { RocFormData } from '../../types/RocFormData'
import { RocFormField } from '../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { createAnswerItemsFromValues, getAnswerValuesFromItems } from '../../../helpers'
import { SharedCareQuestionnaireItem } from '../../../hooks/constants'
import { SectionAdapter } from '../../../adapters/types'

/**
 * Plan of Care subsection adapter for ROC Assessment
 * Handles goals and interventions extraction and transformation
 */
export class PlanOfCareAdapter extends SectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options: AdapterOptions
  ): Partial<RocFormData> {
    // Extract goals from flat answers
    const goals = getAnswerValuesFromItems(
      Object.values(flatAnswers),
      SharedCareQuestionnaireItem('PLAN_OF_CARE_GOALS_GOAL')
    )

    // Extract interventions from flat answers
    const interventionAnswers =
      flatAnswers[
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO
      ]?.answer ?? []
    const interventions = interventionAnswers?.map((interventionAnswerItem) => {
      const interventionInfoItem = interventionAnswerItem.item ?? []

      const interventionId = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INTERVENTION
      )?.at(0)

      const interventionCategory = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_CATEGORY
      )?.at(0)

      const interventionGoal = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_GOAL
      )?.at(0)

      const interventionTask = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TASK
      )?.at(0)

      const interventionIndication = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INDICATION
      )?.at(0)

      const interventionType = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TYPE
      )?.at(0)

      const interventionInstruction = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INSTRUCTION
      )?.at(0)

      const interventionPatientCenteredGoal = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_PATIENT_CENTERED_GOAL
      )?.at(0)

      const interventionAdditionalDetails = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_ADDITIONAL_DETAILS
      )?.at(0)

      const interventionVisits = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_VISITS
      )

      const interventionFrequency = getAnswerValuesFromItems(
        interventionInfoItem,
        RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_FREQUENCY
      )

      return {
        id: interventionId,
        category: interventionCategory,
        interventionGoal: interventionGoal,
        task: interventionTask,
        indication: interventionIndication,
        type: interventionType,
        instruction: interventionInstruction,
        patientCenteredGoal: interventionPatientCenteredGoal,
        additionalDetails: interventionAdditionalDetails,
        visitNumbers: interventionVisits ?? [],
        frequency: interventionFrequency ?? []
      }
    })

    return {
      [RocFormField.GOALS]: goals,
      [RocFormField.INTERVENTIONS]: interventions ?? []
    }
  }

  buildFlatAnswers(formData: RocFormData): FlatAnswerMutation[] {
    const planOfCareAnswers: FlatAnswerMutation[] = []

    // Build goals flat answers
    const goals = formData[RocFormField.GOALS]
    if (goals) {
      planOfCareAnswers.push({
        linkId: RocAssessmentQuestionnaireItem.PLAN_OF_CARE_GOALS_GOAL,
        value: goals?.filter((goal) => goal)
      })
    }

    const interventions = formData[RocFormField.INTERVENTIONS]
    // Build interventions flat answers
    if (interventions) {
      const interventionAnswers = interventions
        .filter(hasSelectedIntervention)
        .map((intervention) => ({
          item: createAnswerItemsFromValues([
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INTERVENTION,
              intervention.id
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_CATEGORY,
              intervention.category
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_GOAL,
              intervention.interventionGoal
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TASK,
              intervention.task
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INDICATION,
              intervention.indication
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_TYPE,
              intervention.type
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_INSTRUCTION,
              intervention.instruction
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_PATIENT_CENTERED_GOAL,
              intervention.patientCenteredGoal
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_ADDITIONAL_DETAILS,
              intervention.additionalDetails
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_VISITS,
              intervention.visitNumbers
            ],
            [
              RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO_FREQUENCY,
              intervention.frequency
            ]
          ])
        }))

      planOfCareAnswers.push({
        linkId: RocAssessmentQuestionnaireItem.PLAN_OF_CARE_PATIENT_INTERVENTIONS_INTERVENTION_INFO,
        value: interventionAnswers ?? undefined
      })
    }

    return planOfCareAnswers
  }
}
